import { FormikErrors, FormikHelpers, useFormik } from 'formik'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Input, PopoverInput } from '@ui/components/Input'
import { Modal } from '@ui/components/Modal'
import { Toggle } from '@ui/components/Toggle/Toggle'
import { Tooltip } from '@ui/components/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useWindowResize } from '@utils/domUtils'
import { isAllowedNumericKey } from '@utils/input.utils'
import { isNumber, maxNumber, minNumber } from '@utils/validations'

import { FilterQueryProps } from '@model/FilterFields'
import { IntroducingBrokerRebateGroupDto } from '@model/IntroducingBrokerRebateGroupDto'
import { NameDto } from '@model/NameDto'

import { Box } from '@shared/components/Box'

interface GroupModalProps {
  groupData?: Partial<IntroducingBrokerRebateGroupDto>
  onSubmit: (values: Partial<GroupFormValues>, groupId?: string) => Promise<void>
  isCreateGroup: boolean
  approvalTypes: NameDto[]
  rebatePeriods: NameDto[]
}

export const GroupModal: BaseModalType<GroupModalProps> = (props) => {
  const { groupData, close, onSubmit, approvalTypes, rebatePeriods, isCreateGroup } = props

  const initialValues = useMemo(
    () =>
      groupData
        ? {
            name: groupData?.name ?? '',
            rebateAmountInPercentage: groupData?.rebateAmountInPercentage
              ? groupData?.rebateAmountInPercentage.toString()
              : '',
            rebateApprovalTypeId: groupData?.rebateApprovalType?.id,
            rebatePeriodId: groupData?.rebatePeriod?.id,
            isDefault: groupData?.isDefault ?? false,
          }
        : undefined,
    [groupData]
  )

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const { setFieldValue, errors, values, handleSubmit, setErrors, isSubmitting, resetForm } =
    useGroupForm({
      initialValues: initialValues,
      handleSubmit: async (values, { resetForm, setSubmitting }) => {
        setSubmitting(true)
        await onSubmit(values, groupData?.id)
        setSubmitting(false)
        resetForm()
      },
    })

  // Reset form when modal opens with new data
  useEffect(() => {
    resetForm({ values: initialValues || DEFAULT_VALUES })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData, isCreateGroup])

  const hasErrors = useMemo(() => Object.values(errors).some((error) => !!error), [errors])

  const isApplyDisabled =
    hasErrors ||
    isSubmitting ||
    !values.name ||
    !values.rebateApprovalTypeId ||
    values.rebatePeriodId == null ||
    !values.rebateAmountInPercentage

  const handleChange = (key: string, value: any) => {
    setFieldValue(key, value)

    if (hasErrors) {
      setErrors({
        ...errors,
        [key]: undefined,
      })
    }
  }

  return (
    <Modal key={`${isCreateGroup ? 'create' : 'edit'}-${groupData?.id || 'new'}`}>
      <Modal.Header canClose={!isMobile}>
        {isCreateGroup
          ? t('IB.RebatesPage.GroupsPage.New Group')
          : t('IB.RebatesPage.GroupsPage.Update Group settings')}
      </Modal.Header>
      <Modal.Body>
        <Input
          value={values.name}
          label={t('IB.RebatesPage.GroupsPage.Group name')}
          onChange={(e) => {
            handleChange('name', e.target.value)
          }}
          hint={errors.name}
          fullWidth
          error={!!errors.name}
        />
        <PopoverInput
          value={values.rebateApprovalTypeId}
          onChange={(option) => handleChange('rebateApprovalTypeId', option.id)}
          options={approvalTypes}
          label={t('IB.RebatesPage.GroupsPage.Group type')}
          isFullWidth
          hint={errors.rebateApprovalTypeId}
          error={!!errors.rebateApprovalTypeId}
        />
        <PopoverInput
          value={values.rebatePeriodId}
          onChange={(option) => handleChange('rebatePeriodId', option.id)}
          options={rebatePeriods}
          label={t('IB.RebatesPage.GroupsPage.Payout period')}
          isFullWidth
          hint={errors.rebatePeriodId}
          error={!!errors.rebatePeriodId}
        />
        <Input
          value={values.rebateAmountInPercentage}
          label={t('IB.RebatesPage.Rebate amount in %')}
          onChange={(e) => handleChange('rebateAmountInPercentage', e.target.value)}
          fullWidth
          hint={errors.rebateAmountInPercentage}
          error={!!errors.rebateAmountInPercentage}
          onKeyDown={(e) => !isAllowedNumericKey(e) && e.preventDefault()}
        />
        <Box my='x4' flex align='center' justify='space-between' fullWidth>
          <Box flex align='center' gap='x8'>
            <Body2 medium>{t('IB.RebatesPage.GroupsPage.Make group default')}</Body2>

            <Tooltip
              content={t(
                'IB.RebatesPage.GroupsPage.If selected, all new TAs will be automatically assigned to that Group'
              )}
            >
              <ActionIcon icon={InfoIcon} size='x20' />
            </Tooltip>
          </Box>
          <Toggle
            name='isDefaultGroup'
            value={!!values.isDefault}
            onChange={() => handleChange('isDefault', !values.isDefault)}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        {isMobile && <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>}
        <Modal.Footer.Primary onClick={() => handleSubmit()} disabled={isApplyDisabled}>
          {isCreateGroup ? t('IB.RebatesPage.GroupsPage.Create a Group') : t('Apply')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

export interface GroupFormValues {
  name: string
  rebateApprovalTypeId: number
  rebatePeriodId: number
  rebateAmountInPercentage: string
  isDefault: boolean
}

interface GroupFormParams {
  handleSubmit: (
    values: Partial<GroupFormValues>,
    formikHelpers: FormikHelpers<Partial<GroupFormValues>>
  ) => void
  initialValues?: Partial<GroupFormValues>
}

const DEFAULT_VALUES: Partial<GroupFormValues> = {
  name: '',
  rebateApprovalTypeId: undefined,
  rebatePeriodId: undefined,
  rebateAmountInPercentage: '',
  isDefault: false,
}

export const useGroupForm = ({
  handleSubmit,
  initialValues = DEFAULT_VALUES,
}: GroupFormParams): ReturnType<typeof useFormik<Partial<GroupFormValues>>> => {
  const { t } = useTranslation()

  return useFormik<Partial<GroupFormValues>>({
    initialValues: initialValues,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validate: (values) => {
      const errors: FormikErrors<FilterQueryProps> = {}

      if (!values.name) {
        errors.name = t('Validation.Required')
      }

      if (!values.rebateApprovalTypeId) {
        errors.rebateApprovalTypeId = t('Validation.Required')
      }

      if (values.rebatePeriodId == null) {
        errors.rebatePeriodId = t('Validation.Required')
      }

      if (!values.rebateAmountInPercentage) {
        errors.rebateAmountInPercentage = t('Validation.Required')
      }

      if (values.rebateAmountInPercentage && !isNumber(values.rebateAmountInPercentage)) {
        errors.rebateAmountInPercentage = t('Validation.Number')
      }

      if (
        values.rebateAmountInPercentage &&
        (minNumber(values.rebateAmountInPercentage, 0.1) ||
          maxNumber(values.rebateAmountInPercentage, 100))
      ) {
        errors.rebateAmountInPercentage = t(
          'IB.RebatesPage.ApprovalsPage.Please enter a valid percentage between 0 and 100'
        )
      }

      return errors
    },
    validateOnChange: false,
  })
}
